import { ApiService } from 'src/app/services/api.service';
import { LocatedVesselRecord } from '../../shared/contracts/vessel.types';

/**
 * Voyage Radar's SAVED VESSELS store — the three questions the shared located
 * module asks about persistence, answered in VR's dialect.
 *
 * Plain functions rather than a shared interface, for the reason recorded in
 * decision D8 and repeated in `vr-snapshot-archive.gateway.ts`: saving a hull
 * against a task+sensor document is Voyage Radar's arrangement, no other
 * provider is known to need it, and inventing a `SavedVesselsGateway`
 * vocabulary would be designing for a variation that may never arrive. The
 * shared store keeps what is genuinely its own — the record shape, `kept`, and
 * re-pushing the panel rows — and is TOLD these three, exactly as
 * `TargetController` is told `ownScene`.
 */

/** True when the hull has a saved record on this sensor (drawn or not). */
export function vrIsSaved(sensor: any, mmsi: string): boolean {
  const m = String(mmsi ?? '').trim();
  return (sensor?.vesselHistory ?? []).some((v: any) => String(v?.mmsi) === m);
}

/**
 * Persist a located vessel, resolving to the kept-ness the store should record
 * — or to NULL when nothing was attempted at all.
 *
 * The three-state result is not fussiness: "the provider was asked and said
 * no" and "the provider was never asked" are different facts, and the store
 * acts differently on them. Without a task and sensor there is no document to
 * file against, so the record is left exactly as it was rather than being
 * marked unsaved — which is what the monolith's early return did.
 *
 * Best-effort otherwise: the marker is already on the map either way, and a
 * failure simply leaves the vessel bar reporting it as not kept. The local
 * sensor doc is mirrored on success so a re-hydrate within this session agrees
 * with the server without a refetch.
 */
export function vrSaveVessel(
  api: ApiService,
  scope: { taskid: any; sensorId: any; sensor: any },
  record: LocatedVesselRecord,
): Promise<boolean | null> {
  const { taskid, sensorId, sensor } = scope;
  if (!taskid || !sensorId) return Promise.resolve(null);
  return api
    .saveVoyageRadarVessel({
      taskid,
      sensorId,
      mmsi: record.mmsi,
      source: record.source ?? 'search',
      query: record.query ?? undefined,
      identity: {
        shipName: record.name ?? undefined,
        imoNumber: record.imoNumber ?? undefined,
        callSign: record.callSign ?? undefined,
        shipType: record.shipType ?? undefined,
        flag: record.flag ?? undefined,
        country: record.country ?? undefined,
      },
      asOf: {
        latitude: record.lat ?? undefined,
        longitude: record.lng ?? undefined,
        time_utc: record.timeUtc ?? undefined,
        navigationalStatus: record.navStatus ?? undefined,
        waterBody: record.waterBody ?? undefined,
        sog: record.sog ?? undefined,
        cog: record.cog ?? undefined,
        heading: record.heading ?? undefined,
      },
    })
    .toPromise()
    .then((res: any) => {
      // Mirror into the local sensor doc so a re-hydrate within this session
      // agrees with the server without a refetch.
      if (sensor && res?.vessel) {
        const list = Array.isArray(sensor.vesselHistory) ? sensor.vesselHistory : [];
        const i = list.findIndex((v: any) => String(v?.mmsi) === record.mmsi);
        if (i >= 0) list[i] = res.vessel;
        else list.push(res.vessel);
        sensor.vesselHistory = list;
      }
      return true;
    })
    .catch(() => false);
}

/**
 * Forget a vessel — server-side too, so a reload does not resurrect it.
 *
 * `wasKept` is the store's own verdict on whether this record was ever
 * persisted; combined with the local list it decides whether there is anything
 * to forget at all. Fire-and-forget: the row is already gone from the panel,
 * and a failed delete surfaces on the next load rather than as a dialog over a
 * tidying action.
 */
export function vrRemoveVessel(
  api: ApiService,
  scope: { taskid: any; sensorId: any; sensor: any },
  mmsi: string,
  wasKept: boolean,
): void {
  const { taskid, sensorId, sensor } = scope;
  const inHistory = (sensor?.vesselHistory ?? []).some(
    (v: any) => String(v?.mmsi) === mmsi,
  );
  if ((wasKept || inHistory) && taskid && sensorId) {
    api
      .removeVoyageRadarVessel({ taskid, sensorId, mmsi })
      .toPromise()
      .catch(() => undefined);
    if (Array.isArray(sensor?.vesselHistory)) {
      sensor.vesselHistory = sensor.vesselHistory.filter(
        (v: any) => String(v?.mmsi) !== mmsi,
      );
    }
  }
}
